import { Dialog } from "construct-ui";
import moment from "moment";
import paginationComponent from "../../../components/pagination/pagination.component";
import SingleSelectComponent from "../../../components/single-select/single-select.component";
import MultiSelectComponent from "../../../components/multi-select/multi-select.component"
import codeMasterModel from "../../../models/common/code-master.model";
import FixtureModel from "../../../models/planogram/fixture.model";
import ViewBase from "../../view-base.view";
import FixtureUploadDialog from "./fixture-upload.dialog";
import FixtureEditDialog from "./fixture-edit.dialog";
import loginModel from "../../../models/login.model";
import { codeMasterType } from "../../../config/constants";
import { formats as FORMATS } from "../../../config/constants";

/**
 * Fixture View
 * 
 * View page that displays all fixture and their respsective planogram type/division
 * 
 * @author Molly Liu
 */

export default function FixtureView() {
  ViewBase.call(this);

  const DEFAULT_STATUS = {
    sequence: "1",
    code: "1",
    name: "Active"
  };

  const DEFAULT_FILTERS = {
    divGroup: [],
    fixtureName: '',
    status: DEFAULT_STATUS,
    imageType: ''
  };

  const DEFAULT_PAGINATION = {
    pageSize: 10,
    pageIndex: 1,
    totalItems: 0
  };

  let filters = structuredClone(DEFAULT_FILTERS);
  let currentSearchParams = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);

  let planogramDivGroups = [];
  let planogramTypeList = [];
  let statuses = [];
  let isFixtureUploadOpen = false;
  let isFixtureEditOpen = false;

  let fixtures = [];
  let selectedFixture = {};

  const toQueryParams = (params) => {
    return Object.assign(
      {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
        fixtureName: params.fixtureName,
        imageType: params.imageType,
        divGroup: params.divGroup.map((x) => x.code).join('^') || "",
        status: Number(params.status.code)
      }
    );
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    pagination = structuredClone(DEFAULT_PAGINATION);
    currentSearchParams = structuredClone(filters)
    getFixtureList();
  }

  const getFixtureList = () => {
    this.showLoading();
    FixtureModel.getFixtureList(toQueryParams(currentSearchParams))
      .then((result) => {
        if (result.data) {
          fixtures = result.data;
          pagination.pageIndex = result.pageIndex;
          pagination.pageSize = result.pageSize;
          pagination.totalItems = result.total;
        }
      })
      .catch((error) => {
        this.handleError(error)
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getStatuses = () => {
    codeMasterModel.getByCategoryV2({ category: 'CommonStatus' })
      .then((result) => {
        statuses = result
      })
      .catch((error) => {
        this.handleError(error);
      })
  }

  const getPlanogramDivGroups = () => {
    codeMasterModel.getCodemasterCategoryData(loginModel.credentials.country, codeMasterType.planogramDivGroup)
      .then(result => {
        planogramDivGroups = result
      })
      .catch(error => {
        this.handleError(error)
      })
  }

  const getPlanogramTypes = () => {
    codeMasterModel.getByCategoryV2({ category: codeMasterType.planogramType })
      .then(result => {
        planogramTypeList = result
      })
      .catch(error => {
        this.handleError(error)
      })
  }

  const downloadFixture = () => {
    this.showLoading();
    currentSearchParams = structuredClone(filters)
    const FILE_NAME = `Must-Have-SKU-POP-Image-${moment(new Date()).format(FORMATS.apiDateFormat)}.xlsx`
    FixtureModel.downloadFixture(FILE_NAME, toQueryParams(currentSearchParams))
      .catch((error => {
        this.handleError(error);
      }))
      .finally(() => {
        this.hideLoading();
      })
  }

  const clearFilter = () => {
    filters = structuredClone(DEFAULT_FILTERS);
    pagination = structuredClone(DEFAULT_PAGINATION);
  }

  const openFixtureUploadModal = () => {
    isFixtureUploadOpen = true;
  }

  const closeFixtureUploadModal = () => {
    isFixtureUploadOpen = false;
  }

  const onSaveComplete = () => {
    isFixtureUploadOpen = false;
    getFixtureList();
  }

  const onUpdateComplete = (result) => {
    const resultObj = result[0];
    if (resultObj.message === "Success") {
      isFixtureEditOpen = false;
      getFixtureList();
    } else {
      this.showErrorMessage(this.getText(resultObj.code, resultObj.message));
    }
  }

  const openFixtureEditModal = (data) => {
    selectedFixture = data;
    isFixtureEditOpen = true;
  }

  const closeFixtureEditModal = () => {
    isFixtureEditOpen = false;
  }

  return {
    oninit: () => {
      getPlanogramDivGroups();
      getStatuses();
      getFixtureList();
      getPlanogramTypes();
    },
    view: () => {
      return [
        /* SEARCH */
        m('.card.mb-3',
          m('.card-body',
            m('form.needs-validation[novalidate].row.g-2', { onsubmit: handleFormSubmit },
              m(".col-md-3.col-lg-3.position-relative",
                m("label.form-label", this.getText("LT00000246", "Division Group")),
                m(MultiSelectComponent, {
                  id: "planogramDivGroup",
                  items: planogramDivGroups,
                  selectedItems: filters?.divGroup,
                  filterable: true,
                  onSelect: (selectedItems) => {
                    filters.divGroup = selectedItems;
                  }
                })
              ),
              m('.col-md-3.col-lg-3.position-relative',
                m('label.form-label', this.getText("LT00001198", "Image Type")),
                m(SingleSelectComponent, {
                  selectedItem: filters.imageType,
                  checkmark: true,
                  items: planogramTypeList,
                  filterable: false,
                  keyOnly: true,
                  onSelect: e => {
                    filters.imageType = e;
                  }
                }),
              ),
              m('.col-md-3.col-lg-3.position-relative',
                m('label.form-label[for="fixtureNameInput"]', this.getText('LT00001197', 'Image Name')),
                m('input#fixtureNameInput', {
                  class: 'form-control form-control-bg',
                  placeholder: this.getText('LT00001197', 'Image Name'),
                  type: 'text',
                  maxlength: 200,
                  value: filters.fixtureName,
                  onchange: (e) => { filters.fixtureName = e.target.value }
                })
              ),

              m('.col-md-3.col-lg-3.position-relative',
                m('label.form-label[for="statusSelect"]', this.getText('LT00000781', 'Status')),
                m(SingleSelectComponent, {
                  id: 'statusSelect',
                  items: statuses,
                  selectedItem: filters.status,
                  onSelect: (selectedItem) => {
                    filters.status = selectedItem
                  }
                })
              ),
              m('.col-12.d-flex.justify-content-end.gap-2',
                m('button.btn.btn-primary[type="submit"]', this.getText("LT00000001", "Search")),
                m('button.btn.btn-white',
                  {
                    onclick: () => {
                      clearFilter();
                    }
                  },
                  this.getText("LT00000031", "Clear")
                )
              )
            )
          )
        ),

        /* TABLE */
        m('.card',
          m('.card-body',
            m('.row.g-2',
              m('.col-12.d-flex.justify-content-end',
                m('button.btn.btn-primary.me-2', {
                  onclick: openFixtureUploadModal,
                  // disabled: fixtures.length === 0
                }, m('i.bi.bi-file-earmark-arrow-up-fill'),
                  this.getText('LT00000290', 'Upload')),

                m('button.btn.btn-primary', {
                  onclick: downloadFixture,
                  disabled: fixtures.length === 0
                }, m('i.bi.bi-file-earmark-arrow-down-fill'),
                  this.getText('LT00000018', 'Excel Download'))
              ),
              m('.col-12',
                m('.table-responsive',
                  m('table.table.table-sm',
                    m('thead',
                      m('tr.align-middle.center',
                        m('th', this.getText('LT00001196', 'Image ID')),
                        m('th', this.getText('LT00001197', 'Image Name')),
                        m('th', this.getText('LT00001198', 'Image Type')),
                        m('th', this.getText('LT00000246', 'Division Group')),
                        m('th', this.getText('LT00001311', 'Image')),
                        m('th', this.getText('LT00000015', 'Status')),
                        m('th', this.getText('LT00000128', 'Edit'))
                      )
                    ),
                    m('tbody',
                      fixtures?.length === 0 &&
                      m("tr",
                        { class: "align-middle" },
                        m("td",
                          { colspan: "100%" },
                          this.getText("EM00000141", "No records found.")
                        )
                      ),
                      fixtures?.length > 0 &&
                      fixtures.map((data,) => {
                        const active = data.status === 1;
                        return m('tr.align-middle.center',
                          m('td', m('span.cursor-text', data.rowID)),
                          m('td', m('span.cursor-text', data.fixtureName)),
                          m('td', m('span.cursor-text', data.planogramTypeName)),
                          m('td', m('span.cursor-text', data.divGroupName)),
                          m('td', m('img.img-thumbnail', { src: data.photo, alt: "Image", style: { width: "60px", height: '40px', "object-fit": 'cover' } })),
                          m('td', m(`span.badge.rounded-pill${active ? '.green-bg-status' : '.danger-bg-status'}`, active ? this.getText('LT00000025', 'Active') : this.getText('', 'Inactive'))),
                          data.fixtureName !== "NO PLANOGRAM STORE" && m("td", m('i.bi.bi-pencil-square.list-action-icon[title="Edit"]',
                            {
                              onclick: () => {
                                openFixtureEditModal(data);
                              },
                            }))
                        )
                      }))
                  )
                )
              )
            ),
            m('.col-12', m(paginationComponent,
              {
                currentPage: pagination.pageIndex,
                pageSize: pagination.pageSize,
                totalItems: pagination.totalItems,
                onPageChanged: (pageIndex) => {
                  pagination.pageIndex = pageIndex;
                  getFixtureList();
                },
                onPageSizeChanged: (_, pageSize) => {
                  pagination.pageIndex = DEFAULT_PAGINATION.pageIndex
                  pagination.pageSize = pageSize;
                  getFixtureList();
                }
              }
            ))
          )
        ),
        isFixtureUploadOpen &&
        m(Dialog, {
          isOpen: isFixtureUploadOpen,
          transitionDuration: 200,
          closeOnOutsideClick: false,
          onClose: closeFixtureUploadModal,
          title: this.getText("LT00000290", "Upload"),
          content: m(FixtureUploadDialog, { onClose: closeFixtureUploadModal, onSaveComplete: onSaveComplete }),
          "class": "cui-dialog",
        }),
        isFixtureEditOpen &&
        m(Dialog, {
          isOpen: isFixtureEditOpen,
          transitionDuration: 200,
          closeOnOutsideClick: false,
          onClose: closeFixtureEditModal,
          title: this.getText("LT00000128", "Edit"),
          content: m(FixtureEditDialog, { onClose: closeFixtureEditModal, record: selectedFixture, onSave: onUpdateComplete }),
          "class": "cui-dialog",
        }),
        this.getBaseViews()
      ];
    }
  };
}
